import React, { Fragment } from 'react';
import { Box, Flex, Table, Td, Th, Tr, Tbody, Thead, Select, Checkbox, Button } from '@chakra-ui/react';
import ReactPaginate from 'react-paginate';
import { find, isEmpty } from 'lodash';
import EmptyState from '@components/emptyState';
import { ConditionalComponent, IterationComponent } from '@components/functionComponent';

import './styles.scss';
import { TextBold, TextRegular } from '@components/typography';
import SkeletonComponent from '@components/skeleton';
import SortTableIcon from './components/sortIcon';
import { ChevronRightIcon, ChevronLeftIcon } from '@assets/icons/index';

const TableTemplate2 = ({
    tableList,
    data,
    totalPage,
    page,
    handlePageClick,
    isDynamicTable,
    totalData,
    isPagination,
    overflowTable = 'hidden',
    emptyStateBottomComponent,
    isLoading,
    onSort,
    sortType,
    onClickByRow,
    selectedRow,
    limit,
    onChangeBulkCheckbox,
    onCheckAllRow,
    onChangeLimit,
    isSearch,
    noResultSearchImage,
    initialStateImage,
    description = (
        <>
            Saat ini, data sell-in dan sell-out belum tersedia untuk bulan <b>Agustus 2024.</b>
        </>
    ),
}) => {
    return (
        <Box className="table-template2__container" data-testid="table-template2">
            <Box className="table-container" overflow={overflowTable} maxH={isLoading ? '622px' : 'unset'}>
                <Table w={isDynamicTable ? 'max-content' : '100%'}>
                    {/** --------------------------  TABLE HEAD -------------------------- */}
                    <ConditionalComponent statement={true}>
                        <Thead>
                            <Tr className="row-head__container">
                                <IterationComponent
                                    arr={tableList}
                                    render={({
                                        name,
                                        width,
                                        isDivider,
                                        value,
                                        justifyContent = 'flex-start',
                                        withSort,
                                        isCheckbox,
                                    }) => {
                                        const isIndeterminate =
                                            selectedRow?.length > 0 &&
                                            selectedRow?.length !== limit &&
                                            selectedRow?.filter((item) => find(data, { id: item?.id }))?.length > 0;

                                        const isChecked = selectedRow?.length === limit;

                                        const onClickCheckbox = () => {
                                            if (isIndeterminate || isChecked) {
                                                onChangeBulkCheckbox(
                                                    selectedRow?.filter((item) =>
                                                        isEmpty(find(data, { id: item?.id })?.id),
                                                    ),
                                                );
                                            } else onCheckAllRow();
                                        };

                                        const Component = withSort ? Button : Flex;
                                        const attributes = withSort
                                            ? { variant: 'unstyled', paddingX: 0, height: 'fit-content' }
                                            : {};
                                        return (
                                            <Fragment>
                                                <Th
                                                    className={`table-head__item ${isDivider && 'divider'}`}
                                                    value
                                                    width={`${width}px`}
                                                    maxWidth={`${width}px`}
                                                    key={value}
                                                >
                                                    <Component
                                                        {...attributes}
                                                        gap="8px"
                                                        justifyContent={justifyContent}
                                                        alignItems="center"
                                                        onClick={() => withSort && onSort(value)}
                                                    >
                                                        <ConditionalComponent
                                                            statement={!isCheckbox}
                                                            fallback={
                                                                <Checkbox
                                                                    isIndeterminate={isIndeterminate}
                                                                    isChecked={isChecked}
                                                                    onChange={onClickCheckbox}
                                                                />
                                                            }
                                                        >
                                                            <TextBold className="head-title">{name}</TextBold>
                                                            <SortTableIcon
                                                                sortType={sortType}
                                                                withSort={withSort}
                                                                value={value}
                                                            />
                                                        </ConditionalComponent>
                                                    </Component>
                                                </Th>
                                            </Fragment>
                                        );
                                    }}
                                />
                            </Tr>
                        </Thead>
                    </ConditionalComponent>

                    {/** --------------------------  TABLE COLUMN-------------------------- */}

                    <ConditionalComponent statement={data?.length > 0 && !isLoading}>
                        <Tbody>
                            <IterationComponent
                                arr={data}
                                render={(values, index) => {
                                    const isSelected = find(selectedRow, { id: values?.id });

                                    return (
                                        <Tr
                                            className={`row-column__container ${
                                                isSelected && 'row-column__container--selected'
                                            }`}
                                            key={index}
                                        >
                                            <IterationComponent
                                                arr={tableList}
                                                render={(item) => (
                                                    <Td
                                                        key={item?.value}
                                                        className={`table-column__item ${item.isDivider && 'divider'}`}
                                                        maxWidth={item?.width}
                                                        onClick={(event) => {
                                                            // detect if clicking around the checkbox, so if users missclick the checkbox area, it still triggers the checkbox
                                                            if (item.isCheckbox) {
                                                                onClickByRow
                                                                    ? onClickByRow({ values, index, event })
                                                                    : undefined;
                                                            }
                                                        }}
                                                        onChange={(event) =>
                                                            onClickByRow
                                                                ? onClickByRow({ values, index, event })
                                                                : undefined
                                                        }
                                                    >
                                                        {item.component({ value: values[item.value], values, index })}
                                                    </Td>
                                                )}
                                            />
                                        </Tr>
                                    );
                                }}
                            />
                        </Tbody>
                    </ConditionalComponent>

                    <ConditionalComponent statement={isLoading}>
                        <Tbody>
                            <IterationComponent
                                arr={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                                render={(_, index) => (
                                    <Tr className="row-column__container row-skeleton" key={index}>
                                        <IterationComponent
                                            arr={tableList}
                                            render={(item) => (
                                                <Td
                                                    key={item?.value}
                                                    className={`table-column__item ${item.isDivider && 'divider'}`}
                                                    maxWidth={item?.maxWidth}
                                                >
                                                    <Flex width="100%" justifyContent={item?.justifyContent}>
                                                        <ConditionalComponent
                                                            statement={item?.value !== 'devider'}
                                                            fallback={<Box className="devider-component" />}
                                                        >
                                                            <SkeletonComponent
                                                                width="100%"
                                                                maxWidth={item?.skeleton?.width ?? '100px'}
                                                                height={item?.skeleton?.height ?? '20px'}
                                                            />
                                                        </ConditionalComponent>
                                                    </Flex>
                                                </Td>
                                            )}
                                        />
                                    </Tr>
                                )}
                            />
                        </Tbody>
                    </ConditionalComponent>
                </Table>

                {!isLoading ? (
                    isSearch?.length > 0 && isEmpty(data) ? (
                        <EmptyState
                            text={`Tidak Ada Hasil untuk "${isSearch}"`}
                            image={noResultSearchImage}
                            description={
                                <TextRegular>
                                    Periksa kembali ejaan, atau coba sesuaikan kembali pencarian dan filter.
                                </TextRegular>
                            }
                        />
                    ) : isEmpty(data) ? (
                        <EmptyState
                            image={initialStateImage}
                            text="Data Tidak Tersedia"
                            emptyStateBottomComponent={emptyStateBottomComponent}
                            description={<TextRegular>{description}</TextRegular>}
                        />
                    ) : null
                ) : null}
            </Box>

            <ConditionalComponent statement={isPagination}>
                <Box className="section-pagination__container">
                    <Flex className="pagination__info">
                        <TextRegular>Menampilkan </TextRegular>
                        <Select defaultValue={limit} className="pagination-limit__box" onChange={onChangeLimit}>
                            <IterationComponent
                                arr={[totalData ?? 0, 10, 30, 50, 70, 100]}
                                render={(item) => <option value={item}>{item}</option>}
                            />
                        </Select>
                        <TextRegular> dari total {totalData} Data</TextRegular>
                    </Flex>

                    <Flex className="pagination-container">
                        <ReactPaginate
                            previousLabel={
                                <Button variant={'unstyled'} paddingInline={'4px'} gap="8px" disabled={page - 1 == 0}>
                                    <ChevronLeftIcon />
                                    <TextRegular className="next-button">Prev</TextRegular>
                                </Button>
                            }
                            nextLabel={
                                <Button
                                    variant={'unstyled'}
                                    paddingInline={'4px'}
                                    gap="8px"
                                    disabled={page >= totalPage}
                                >
                                    <TextRegular className="next-button">Next</TextRegular>
                                    <ChevronRightIcon />
                                </Button>
                            }
                            onPageChange={handlePageClick}
                            pageRangeDisplayed={3}
                            marginPagesDisplayed={1}
                            pageCount={totalPage}
                            forcePage={page - 1}
                        />
                    </Flex>
                </Box>
            </ConditionalComponent>
        </Box>
    );
};

export default TableTemplate2;
